import { useCallback } from "react";
import usePointsConverter from "./usePointsConverter";

export default function useCanvasSizing() {
    const { percentToPixel } = usePointsConverter();

  const calculateCanvasSize = useCallback((imageW, imageH) => {
    if (!imageW || !imageH) return { w: 0, h: 0 };

    const screenW = typeof window !== "undefined" ? window.innerWidth : 0;
    const screenH = typeof window !== "undefined" ? window.innerHeight : 0;
    const headerH = 70;
    const consoleW = 160;
    const consoleH = 74;
    const padding = 24;
    let maxH = 0;
    let maxW = 0;
    
    if (screenW > screenH) { // horizontal screen
      if (imageW < imageH) { // vertical picture
        maxH = screenH - padding * 2 - headerH;
        maxW = Math.round((imageW * maxH) / imageH);
      } else { // horizontal picture
        maxW = screenW - consoleW - padding * 2;
        maxH = Math.round((imageH * maxW) / imageW - padding * 2);
        // proportions are broken
        if (maxH > screenH - padding - headerH) {
          maxH = screenH - padding - headerH;
          maxW = Math.round((imageW * maxH) / imageH);
        }
      }
    } else { // vertical screen
      if (imageW < imageH) { // vertical picture
        maxH = screenH - padding * 2 - headerH - consoleH;
        maxW = Math.round((imageW * maxH) / imageH);
      } else { // horizontal picture
        maxW = screenW - padding * 2;
        maxH = Math.round((imageH * maxW) / imageW);
      }
    }

    return { w: maxW, h: maxH };
  }, []);

  const calculatePolygonSize = useCallback((points, w, h) => {
    if (!Array.isArray(points) || !w || !h) return { w: 0, h: 0 };
    const polygon = points.reduce((a, b) => a.concat(b), []);
    const xs = polygon.filter((_, i) => i % 2 === 0);
    const ys = polygon.filter((_, i) => i % 2 === 1);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const wPx = maxX - minX;
    const hPx = maxY - minY;
    return {
      w: parseFloat(((wPx * 100) / w).toFixed(2)),
      h: parseFloat(((hPx * 100) / h).toFixed(2)),
    };
  }, []);

  const calculateBackgroundSize = useCallback((canvasW, canvasH, polygonW, polygonH) => {
    if (!canvasW || !canvasH || !polygonW || !polygonH) return { w: 0, h: 0 };
    return {
      w: Math.round((canvasW * 100) / polygonW),
      h: Math.round((canvasH * 100) / polygonH),
    };
  }, []);

  const calculateBackgroundPosition = useCallback((points, w, h) => {
    if (!Array.isArray(points) || !w || !h) return { x: 0, y: 0 };
    let xPoint = points[0][0];
    let yPoint = points[0][1];
    points.forEach(point => {
      if (point[0] <= xPoint) xPoint = point[0];
      if (point[1] <= yPoint) yPoint = point[1];
    });

    return {
      x: Math.round((xPoint * w) / 100),
      y: Math.round((yPoint * h) / 100),
    };
  }, []);

  const calculateArea = useCallback((points, w, h, position) => {

    if (!Array.isArray(points) || !w || !h || !position) {
      return [];
    }
    const pointsPx = percentToPixel(points, w, h).map(p => [p[0] - position.x, p[1] - position.y]);
    return pointsPx;
  }, []);

  return {
    calculateCanvasSize,
    calculatePolygonSize,
    calculateBackgroundSize,
    calculateBackgroundPosition,
    calculateArea,
  };
}